import {
  DashboardState,
  ECardinality,
  ESdChangeReason,
  IReportParameter,
  ISymbol,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import { Key } from "react";
import isEqual from "react-fast-compare";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { ReportParameterHelper } from "../../../../shared/utility/report-parameter-helper";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import ActionParameterTitle from "./action-parameter-title";
import ActionSectionEmptyState from "./action-section-empty-state";
import { ActionSectionTitle } from "./action-section-header";
import { SymbolPicker } from "./symbol-picker";

interface IReportParametersPanelProps {}

interface IReportParametersPanelState {
  parameters: IReportParameter[];
  activeKeys: string[];
}

class ReportParametersPanel extends PureSdComponent<
  IReportParametersPanelProps & IWithSDProps,
  IReportParametersPanelState
> {
  constructor(props: IReportParametersPanelProps & IWithSDProps) {
    super(props);
    this.state = { parameters: [], activeKeys: [] };
  }

  componentDidMount() {
    this.refreshParameters();
  }

  componentDidUpdate(prevProps: IReportParametersPanelProps & IWithSDProps) {
    if (
      prevProps.sd.selectedKuikaIds !== this.props.sd.selectedKuikaIds ||
      prevProps.sd.changeReason !== this.props.sd.changeReason
    ) {
      this.refreshParameters();
    }
  }

  refreshParameters = () => {
    if (this.props.sd.dashboardState !== DashboardState.reportDesigner) return;
    if (this.props.sd.selectedKuikaIds[0] !== SpecialDIIds.rootDI.toString()) return;

    const rootDI = this.props.sd.screenDesignDictionary[SpecialDIIds.rootDI];
    if (!rootDI) return;

    const scannedNames = ReportParameterHelper.scanLabelsForParameters(rootDI);
    const existingParams = this.props.sd.getReportParameters();
    const merged = ReportParameterHelper.mergeParameters(scannedNames, existingParams);

    if (!isEqual(merged, existingParams)) {
      this.props.sd.setReportParameters(merged);
    }

    this.setState({ parameters: merged });
  };

  onParameterSymbolChanged = (paramName: string, symbol: ISymbol) => {
    const params = [...this.state.parameters];
    const idx = params.findIndex((p) => p.name === paramName);
    if (idx === -1) return;
    params[idx] = { ...params[idx], symbol };
    this.setState({ parameters: params });
    this.props.sd.setReportParameters(params);
  };

  onCollapseChange = (paramName: string, keys: Key[]): void => {
    const isOpen: boolean = keys.some((key: Key): boolean => String(key) === "1");
    this.setState((prev) => {
      const filtered = prev.activeKeys.filter((k) => k !== paramName);
      if (isOpen) {
        filtered.push(paramName);
      }
      return { activeKeys: filtered };
    });
  };

  render() {
    if (this.props.sd.dashboardState !== DashboardState.reportDesigner) return null;
    if (this.props.sd.selectedKuikaIds[0] !== SpecialDIIds.rootDI.toString()) return null;

    return (
      // The same section chrome as Actions and Screen Inputs below it: title with its count, the
      // compact empty box, cards on the column's inset. There is no "+": a parameter is written as
      // @name in a label, so the empty box says that instead of offering a link.
      <div className="kuika_Wrapper">
        <div className="kuika_actionPanelWrapperHeader kuika-action-section__header">
          <div className="kuika-action-section__header-row">
            <ActionSectionTitle title="Report Parameters" count={this.state.parameters.length} />
          </div>
        </div>
        {!this.state.parameters.length && (
          <ActionSectionEmptyState
            message="No parameters yet."
            hint="Write @name in a label to add one."
            testId="reportParametersEmptyState"
          />
        )}
        {this.state.parameters.map((param) => (
          <KuikaCollapsePanel
            key={param.name}
            onChange={(keys: Key[]): void => this.onCollapseChange(param.name, keys)}
            destroyInactivePanel={false}
            activeKey={this.state.activeKeys.includes(param.name) ? ["1"] : []}
            iconPlacement="right"
            className="kuika-right-panel__collapse kuika-right-panel__collapse--boxed kuika-right-panel__collapse--action"
            items={[
              {
                key: "1",
                label: (
                  <div
                    className={OVERFLOW_REVEAL_HOST_CLASS}
                    style={{ display: "flex", alignItems: "center", width: "100%" }}
                  >
                    <KuikaTextMedium style={{ display: "block", minWidth: 0, overflow: "hidden" }} title={param.name}>
                      <KuikaOverflowText>{param.name}</KuikaOverflowText>
                    </KuikaTextMedium>
                  </div>
                ),
                children: (
                  <div className="kuika-right-panel__collapse-body">
                    <span
                      style={{ marginBottom: 6, marginTop: 8, overflowWrap: "anywhere" }}
                      className="k_Regular-G1-Left kuika_rightPanelDIName"
                    >
                      <ActionParameterTitle
                        parameter={{ name: param.name, Typename: "String", cardinality: ECardinality.One }}
                      />
                    </span>
                    <SymbolPicker
                      changeType="onBlur"
                      symbol={param.symbol}
                      valueType={"String"}
                      targetCardinality={ECardinality.One}
                      onSelectedSymbol={(symbol: ISymbol) => this.onParameterSymbolChanged(param.name, symbol)}
                    />
                  </div>
                )
              }
            ]}
          />
        ))}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.PropertyFixedValueChanged ||
      changeReason === ESdChangeReason.ChangePropertyValue ||
      changeReason === ESdChangeReason.DragDropDi
    ) {
      return true;
    }
    return false;
  };
}

const reportParametersPanel = withSD<IReportParametersPanelProps>(ReportParametersPanel);
export { reportParametersPanel as ReportParametersPanel };
